import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/supabaseQuery';
import { detectFullGeolocation } from '@/services/ipGeolocation';
import {
  IP_LOOKUP_TIMEOUT_MS,
  readStoredLocationId,
  resolveExploreLocation,
  withTimeout,
  writeStoredLocationId,
  type ExploreLocationSource,
  type ResolvableLocation,
} from '@/lib/exploreDefaultLocation';

export interface ExploreDefaultLocation {
  locationId: number | null;
  source: ExploreLocationSource;
  status: 'detecting' | 'resolved' | 'unresolved';
}

/**
 * Resolve the location Explore should start with. Signed-in users use their
 * saved profile location; everyone else falls back to one IP lookup per visit.
 */
export function useExploreDefaultLocation(
  urlLocationId: number | null,
  locations: ResolvableLocation[],
  ready: boolean,
  resetNonce = 0,
): ExploreDefaultLocation {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const storedLocationId = useMemo(() => readStoredLocationId(), [resetNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsLookup = ready && !authLoading && urlLocationId === null;

  const profileQuery = useQuery({
    queryKey: ['explore-profile-location', userId],
    enabled: needsLookup && !!userId,
    staleTime: Infinity,
    queryFn: async (): Promise<number | null> => {
      const row = await db.fetchOne<{ location_id: number | null }>('users', 'location_id', [
        { column: 'id', operator: 'eq', value: userId },
      ]);
      return row?.location_id ?? null;
    },
  });

  const storedValid = storedLocationId !== null && locations.some((l) => l.location_id === storedLocationId);
  const profileSettled = !userId || profileQuery.isFetched || profileQuery.isError;
  const profileValid =
    !!profileQuery.data && locations.some((l) => l.location_id === profileQuery.data);

  const ipQuery = useQuery({
    queryKey: ['explore-ip-country'],
    enabled: needsLookup && !storedValid && profileSettled && !profileValid,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<string | null> => {
      const geo = await withTimeout(detectFullGeolocation(), IP_LOOKUP_TIMEOUT_MS);
      return geo?.country_code ?? null;
    },
  });

  return useMemo<ExploreDefaultLocation>(() => {
    if (!ready || authLoading) return { locationId: null, source: 'none', status: 'detecting' };
    if (storedLocationId !== null && !storedValid && locations.length > 0) writeStoredLocationId(null);

    const result = resolveExploreLocation(
      {
        urlLocationId,
        storedLocationId: storedValid ? storedLocationId : null,
        profileLocationId: profileQuery.data ?? null,
        ipCountryCode: ipQuery.data ?? null,
      },
      locations,
    );
    if (result.locationId !== null) return { ...result, status: 'resolved' };

    const stillWorking =
      (!!userId && !profileSettled) || (ipQuery.fetchStatus === 'fetching' || (!ipQuery.isFetched && !ipQuery.isError));
    return { locationId: null, source: 'none', status: stillWorking ? 'detecting' : 'unresolved' };
  }, [ready, authLoading, storedLocationId, storedValid, locations, urlLocationId, profileQuery.data,
      ipQuery.data, ipQuery.fetchStatus, ipQuery.isFetched, ipQuery.isError, userId, profileSettled]);
}
